import { defaultOperationDate, toOperationTimestamp } from '@/components/organisms/FishGroupForms';
import type { BioscopeMovement, RelocateFishGroupRequest } from '@/domain/fishGroups';

export interface FormValues {
    targetCageId: string;
    relocationDate: Date | null;
    reason: string;
    notes: string;
}

// A factory, not a constant: the date defaults to today, and a module-level
// constant would freeze "today" for the lifetime of the tab.
export function makeDefaultValues(): FormValues {
    return {
        targetCageId: '',
        relocationDate: defaultOperationDate(),
        reason: '',
        notes: '',
    };
}

interface RelocateRequestOptions {
    /** The bioscopes the operator ticked; sent only with the permission to move them. */
    selectedBioscopeIds: Set<number>;
    hasBioscopeMovePermission: boolean;
    /** Start of the current stay; the move has to land strictly after it. */
    locationFromDate: Date | null;
}

/** The relocate request for the submitted form, or null while no date is picked. */
export function buildRelocateRequest(
    values: FormValues,
    options: RelocateRequestOptions
): RelocateFishGroupRequest | null {
    const { selectedBioscopeIds, hasBioscopeMovePermission, locationFromDate } = options;
    if (!values.relocationDate) return null;
    const bioscopeMovements: BioscopeMovement[] = [];
    if (hasBioscopeMovePermission && selectedBioscopeIds.size > 0) {
        for (const bioscopeId of selectedBioscopeIds) {
            bioscopeMovements.push({
                bioscopeId,
                targetCageId: Number(values.targetCageId),
            });
        }
    }
    return {
        targetCageId: Number(values.targetCageId),
        relocationDate: toOperationTimestamp(values.relocationDate, {
            notBefore: locationFromDate,
        }),
        ...(values.reason.trim() ? { reason: values.reason.trim() } : {}),
        ...(values.notes.trim() ? { notes: values.notes.trim() } : {}),
        ...(bioscopeMovements.length > 0 ? { bioscopeMovements } : {}),
    };
}
